
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Header from "../../Common/Layout/Header";
import SeriesFareTabs from "../../Supplier/SeriesFares/SeriesFareTabs"; // Import the tabs component

const SeriesFareList = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [fares, setFares] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        search: '',
        airline: '',
        status: '',
        from: '',
        to: ''
    });

    const companyId = user?.cid || user?.companyId;

    useEffect(() => {
        if (companyId) {
            fetchFares();
        }
    }, [companyId]);

    const fetchFares = async () => {
        try {
            const response = await apiService.getAll(`${config.GETSERIESFARESBYCOMPANY}?CompanyId=${companyId}`);
            setFares(response || []);
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Format date as 20-DEC-2025
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        
        try {
            const date = new Date(dateString);
            const day = date.getDate().toString().padStart(2, '0');
            
            // Get month abbreviation in uppercase
            const monthAbbr = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
            
            const year = date.getFullYear();
            
            return `${day}-${monthAbbr}-${year}`;
        } catch (error) {
            return "N/A";
        }
    };

    // Format price with "Per Person" label
    const formatPricePerPerson = (price) => {
        if (price === null || price === undefined || price === 0) return "INR 0 Per Person";
        
        const formattedPrice = Math.round(price).toLocaleString('en-IN');
        return `INR ${formattedPrice} Per Person`;
    };

    // Calculate price per seat (per person)
    const calculatePricePerPerson = (totalPrice, totalSeats) => {
        if (!totalPrice || !totalSeats || totalSeats === 0) return 0;
        
        const total = parseFloat(totalPrice);
        const seats = parseInt(totalSeats);
        
        return total / seats;
    };

    // Get status badge class
    const getStatusBadgeClass = (availableSeats, totalSeats, validTo) => {
        if (!availableSeats || availableSeats === 0) {
            return "badge bg-danger";
        }
        
        const today = new Date();
        const validToDate = new Date(validTo);
        
        if (validToDate < today) {
            return "badge bg-secondary";
        }
        
        const seatPercentage = (availableSeats / totalSeats) * 100;
        
        if (seatPercentage < 10) {
            return "badge bg-warning";
        } else if (seatPercentage < 30) {
            return "badge bg-info";
        } else {
            return "badge bg-success";
        }
    };

    // Get status text
    const getStatusText = (availableSeats, totalSeats, validTo) => {
        if (!availableSeats || availableSeats === 0) {
            return "SOLD OUT";
        }
        
        const today = new Date();
        const validToDate = new Date(validTo);
        
        if (validToDate < today) {
            return "EXPIRED";
        }
        
        const seatPercentage = (availableSeats / totalSeats) * 100;
        
        if (seatPercentage < 10) {
            return "ALMOST SOLD";
        } else if (seatPercentage < 30) {
            return "LIMITED";
        } else {
            return "AVAILABLE";
        }
    };

    // Refresh data
    const handleRefresh = () => {
        setLoading(true);
        fetchFares();
    };

    // Filter fares based on filter criteria
    const filteredFares = fares.filter(fare => {
        // Search filter
        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            const route = `${fare.from}${fare.to}`.toLowerCase();
            const airline = (fare.airlineCode || '').toLowerCase();
            const cabinClass = (fare.cabinClass || '').toLowerCase();
            
            if (!route.includes(searchTerm) && 
                !airline.includes(searchTerm) && 
                !cabinClass.includes(searchTerm)) {
                return false;
            }
        }
        
        // Airline filter
        if (filters.airline && fare.airlineCode !== filters.airline) {
            return false;
        }
        
        // From filter
        if (filters.from && fare.from !== filters.from) {
            return false;
        }
        
        // To filter
        if (filters.to && fare.to !== filters.to) {
            return false;
        }
        
        return true;
    });

    // Get unique airlines for filter dropdown
    const uniqueAirlines = [...new Set(fares.map(fare => fare.airlineCode).filter(Boolean))];
    
    // Get unique departure airports for filter dropdown
    const uniqueFromAirports = [...new Set(fares.map(fare => fare.from).filter(Boolean))];
    
    // Get unique destination airports for filter dropdown
    const uniqueToAirports = [...new Set(fares.map(fare => fare.to).filter(Boolean))];

    // Clear all filters
    const clearFilters = () => {
        setFilters({
            search: '',
            airline: '',
            status: '',
            from: '',
            to: ''
        });
    };

 

    return (
        <div id="layout-wrapper">
            <Header User={user} />
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <div className="card">
                            <div className="p-2">
                                {/* <SeriesFareTabs pageName="SERIESFARELISTS" /> */}
                            </div>
                            <div className="card-body">
                                <div className="row mb-3">
                                    <div className="col-12 d-flex justify-content-between align-items-center">
                                        <h5 className="card-title mb-0">
                                            Manage Series Fares
                                            {filteredFares.length > 0 && (
                                                <span className="text-muted ms-2">
                                                    ({filteredFares.length} fares found)
                                                </span>
                                            )}
                                        </h5>
                                        <div className="d-flex gap-2">
                                        
                                            <button 
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={handleRefresh}
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                                ) : (
                                                    <i className="ri-refresh-line align-bottom me-1"></i>
                                                )}
                                                Refresh
                                            </button>
                                        </div>
                                    </div>
                                </div>

                               

                                {/* Filter Section */}
                                <div className="row mb-3">
                                    <div className="col-md-3">
                                        <input 
                                            type="text" 
                                            className="form-control form-control-sm" 
                                            placeholder="Search fares..." 
                                            value={filters.search}
                                            onChange={(e) => setFilters({...filters, search: e.target.value})}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <select 
                                            className="form-select form-select-sm" 
                                            value={filters.airline}
                                            onChange={(e) => setFilters({...filters, airline: e.target.value})}
                                        >
                                            <option value="">All Airlines</option>
                                            {uniqueAirlines.map(airline => (
                                                <option key={airline} value={airline}>{airline}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <select 
                                            className="form-select form-select-sm" 
                                            value={filters.from}
                                            onChange={(e) => setFilters({...filters, from: e.target.value})}
                                        >
                                            <option value="">All Departures</option>
                                            {uniqueFromAirports.map(airport => (
                                                <option key={airport} value={airport}>{airport}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <select 
                                            className="form-select form-select-sm" 
                                            value={filters.to}
                                            onChange={(e) => setFilters({...filters, to: e.target.value})}
                                        >
                                            <option value="">All Destinations</option>
                                            {uniqueToAirports.map(airport => (
                                                <option key={airport} value={airport}>{airport}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-3 d-flex gap-2">
                                        <button 
                                            className="btn btn-sm btn-danger"
                                            onClick={clearFilters}
                                        >
                                         
                                            Clear Filters
                                        </button>
                                    </div>
                                </div>

                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2">Loading fares...</p>
                                    </div>
                                ) : filteredFares.length === 0 ? (
                                    <div className="text-center py-5">
                                        <div className="display-1 text-muted mb-3">📋</div>
                                        <h5>No Series Fares Found</h5>
                                        <p className="text-muted mb-4">
                                            {fares.length === 0 
                                                ? "You haven't created any series fares yet."
                                                : "No fares match your filters."}
                                        </p>
                                        {fares.length === 0 ? (
                                            <button 
                                                className="btn btn-primary"
                                                onClick={() => navigate('/seriesfare/create')}
                                            >
                                                <i className="ri-add-line align-bottom me-1"></i>
                                                Create Your First Fare
                                            </button>
                                        ) : (
                                            <button 
                                                className="btn btn-secondary"
                                                onClick={clearFilters}
                                            >
                                                Clear Filters
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-centered table-nowrap mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Route</th>
                                                    <th>Airline</th>
                                                    <th>Class</th>
                                                    <th>Departure</th>
                                                    <th>Valid Until</th>
                                                    <th>Seats</th>
                                                    <th>Price Per Person</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredFares.map((fare, index) => {
                                                    const pricePerPerson = calculatePricePerPerson(
                                                        fare.totalPrice, 
                                                        fare.totalSeats
                                                    );
                                                    const statusClass = getStatusBadgeClass(
                                                        fare.availableSeats, 
                                                        fare.totalSeats, 
                                                        fare.validTo
                                                    );
                                                    const statusText = getStatusText(
                                                        fare.availableSeats, 
                                                        fare.totalSeats, 
                                                        fare.validTo
                                                    );
                                                    
                                                    return (
                                                        <tr key={index}>
                                                            <td>
                                                                <div className="fw-semibold">
                                                                    <span className="text-primary">{fare.from}</span> 
                                                                    <i className="ri-arrow-right-line mx-2 text-muted"></i>
                                                                    <span className="text-primary">{fare.to}</span>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className="badge bg-info">
                                                                    {fare.airlineCode || 'N/A'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className="badge bg-secondary">
                                                                    {fare.cabinClass || 'ECONOMY'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="fw-bold">
                                                                    {formatDate(fare.departureDate)}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="fw-bold">
                                                                    {formatDate(fare.validTo)}
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <div className="me-2">
                                                                        <span className="fw-bold text-success">
                                                                            {fare.availableSeats || 0}
                                                                        </span>
                                                                        <span className="text-muted">/</span>
                                                                        <span className="fw-bold">
                                                                            {fare.totalSeats || 0}
                                                                        </span>
                                                                    </div>
                                                                    <div className="progress flex-grow-1" style={{height: '6px', width: '60px'}}>
                                                                        <div 
                                                                            className="progress-bar bg-success" 
                                                                            style={{width: `${((fare.availableSeats || 0) / (fare.totalSeats || 1)) * 100}%`}}
                                                                            role="progressbar"
                                                                        ></div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <div className="fw-bold text-primary">
                                                                    ₹{Math.round(fare.pricePerPerson || 0).toLocaleString('en-IN')}
                                                                    <div className="text-muted small">Per Person</div>
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <span className={statusClass}>
                                                                    {statusText}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <div className="d-flex gap-1">
                                                                    <button 
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        title="View Details"
                                                                    >
                                                                        <i className="ri-eye-line"></i>
                                                                    </button>
                                                                    <button 
                                                                        className="btn btn-sm btn-outline-warning"
                                                                        title="Edit Fare"
                                                                    >
                                                                        <i className="ri-edit-line"></i>
                                                                    </button>
                                                                    <button 
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        title="Delete Fare"
                                                                    >
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                
                                {filteredFares.length > 0 && (
                                    <div className="row mt-3">
                                        <div className="col-12">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="text-muted small">
                                                    Showing {filteredFares.length} of {fares.length} fares
                                                </div>
                                                <div className="text-muted small">
                                                    Last updated: {new Date().toLocaleTimeString()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Add some custom styles */}
            <style jsx>{`
                .card.bg-light {
                    border: 1px solid #e2e8f0;
                    border-radius: 0.5rem;
                }
                
                .card.bg-light .card-body {
                    padding: 1rem;
                }
                
                .progress {
                    background-color: #e9ecef;
                    border-radius: 0.25rem;
                }
                
                .table th {
                    font-weight: 600;
                    background-color: #f8f9fa;
                    border-bottom: 2px solid #e2e8f0;
                    text-transform: uppercase;
                    font-size: 0.75rem;
                    letter-spacing: 0.5px;
                }
                
                .table td {
                    vertical-align: middle;
                    padding: 0.75rem;
                }
                
                .table-hover tbody tr:hover {
                    background-color: #f8fafc;
                }
                
                .badge {
                    font-size: 0.75em;
                    padding: 0.35em 0.65em;
                }
                
                .btn-sm {
                    padding: 0.25rem 0.5rem;
                    font-size: 0.75rem;
                }
            `}</style>
        </div>
    );
};

export default SeriesFareList;